import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { createBooking } from "@/lib/api";
import { IMAGES } from "@/lib/content";

const field =
  "w-full bg-transparent border-b border-[#47394E]/25 py-3 font-body text-[#47394E] outline-none placeholder:text-[#47394E]/40 focus:border-[#B7926A] transition-colors";

export default function Booking() {
  const [form, setForm] = useState({
    name: "", email: "", business: "coaching", date: "", time: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await createBooking(form);
      toast.success(res.message);
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-page" className="pt-24">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="overline text-[#B7926A] mb-6">Booking & Scheduling</p>
          <h1 className="font-display text-5xl sm:text-6xl text-[#47394E] leading-[0.95] tracking-tight">
            <MaskedLines lines={["Let's book", "a call."]} />
          </h1>
          <p className="font-body text-lg text-[#47394E]/75 mt-8 leading-relaxed">
            Request a session for Coaching or the Devoted Wedding Pros Community. Choose a preferred
            date and time — Jamie will confirm by email shortly.
          </p>
          <div className="relative h-64 mt-10 clip-arch overflow-hidden hidden lg:block">
            <img src={IMAGES.desk} alt="" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#47394E] text-[#E8DCCF] p-12 grain"
              data-testid="booking-success"
            >
              <span className="font-display text-6xl text-[#B7926A]">✦</span>
              <h2 className="font-display text-4xl mt-6">Request received.</h2>
              <p className="font-body text-[#E8DCCF]/80 mt-4">
                Thank you, {form.name.split(" ")[0] || "friend"}. Jamie will be in touch to confirm
                your {form.business} session soon.
              </p>
            </motion.div>
          ) : (
            <Reveal>
              <form onSubmit={submit} data-testid="booking-form" className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <input required placeholder="Full name" data-testid="booking-name" value={form.name} onChange={set("name")} className={field} />
                  <input required type="email" placeholder="Email address" data-testid="booking-email" value={form.email} onChange={set("email")} className={field} />
                </div>

                <div>
                  <div className="flex gap-3">
                    {[["coaching", "Coaching"], ["networking", "Devoted Wedding Pros Community"]].map(([v, l]) => (
                      <button
                        type="button"
                        key={v}
                        data-testid={`booking-business-${v}`}
                        onClick={() => setForm((f) => ({ ...f, business: v }))}
                        className={`rounded-full px-6 py-2.5 text-sm font-medium border transition-colors ${
                          form.business === v
                            ? "bg-[#47394E] text-[#E8DCCF] border-[#47394E]"
                            : "border-[#47394E]/30 text-[#47394E] hover:border-[#B7926A]"
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <input required type="date" data-testid="booking-date" value={form.date} onChange={set("date")} className={field} />
                    <label className="overline text-[#9C8199] block mt-2">Preferred date</label>
                  </div>
                  <div>
                    <input required type="time" data-testid="booking-time" value={form.time} onChange={set("time")} className={field} />
                    <label className="overline text-[#9C8199] block mt-2">Preferred time</label>
                  </div>
                </div>

                <textarea rows={4} placeholder="Anything you'd like Jamie to know? (optional)" data-testid="booking-notes" value={form.notes} onChange={set("notes")} className={`${field} resize-none`} />

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="rounded-full bg-[#47394E] text-[#E8DCCF] px-10 py-4 font-medium hover:bg-[#B7926A] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Request session"}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
